import React, { useEffect, useState, useCallback, useRef } from "react";
import { 
  CreateTable,
  SuccessMessage,
  FailMessage,
  Loading,
  FormatValueToDecimal,
  DateSelect
} from "../../common/APICommon";
import { apiUrl } from "../../utils/Configuration";
import NoAccess from "../NoAccess";
import axios from 'axios';
import { motion, AnimatePresence } from "framer-motion";
import * as XLSX from "xlsx";
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AssuredWorkloadIcon from '@mui/icons-material/AssuredWorkload';
import PublishIcon from '@mui/icons-material/Publish';
import CloseIcon from '@mui/icons-material/Close';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import PreviewIcon from '@mui/icons-material/Preview';

export default function BankReconciliation() {
//#region Others
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [failMessage, setFailMessage] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");

  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.checkNo.toLowerCase().includes(lowerValue) ||
        item.referenceNo.toLowerCase().includes(lowerValue)
    );

    setData(filtered);
  };
//#endregion




//#region Variables
  const [selectedData, setSelectedData] = useState(null);
  const today = new Date().toISOString().split("T")[0];
  const [date, setDate] = useState(today);
  const [checkNo, setCheckNo] = useState("");
  const [sequenceNo, setSequenceNo] = useState("");
  const [referenceNo, setReferenceNo] = useState("");
  const [branch, setBranch] = useState("");
  const [transactionCode, setTransactionCode] = useState("");
  const [transactionDescription, setTransactionDescription] = useState("");
  const [withdrawals, setWithdrawals] = useState(0);
  const [deposits, setDeposits] = useState(0);
  const [runningBalance, setRunningBalance] = useState(0);

  const resetVariables = () => {
    setSelectedData(null);
    setDate(today);
    setCheckNo("");
    setSequenceNo("");
    setReferenceNo("");
    setBranch("");
    setTransactionCode("");
    setTransactionDescription("");
    setWithdrawals(0);
    setDeposits(0);
    setRunningBalance(0);
  }
//#endregion




// #region Add function
  const [showAddForm, setShowAddForm] = useState(false);
  
  const openAddForm = () => {
    setShowAddForm(true);
  }
  const closeAddForm = () => {
    setShowAddForm(false);
    resetVariables();
  }

  const handleAddData = async () => {
    const data = {
      date: date,
      checkNo: checkNo,
      sequenceNo: sequenceNo,
      referenceNo: referenceNo,
      branch: branch,
      transactionCode: transactionCode,
      transactionDescription: transactionDescription,
      withdrawals: withdrawals,
      deposits: deposits,
      runningBalance: runningBalance,
    };

    try {
      setLoading(true);

      await axios.post(`${apiUrl}/api/BankReconciliation`, data, {
          headers: { 'Content-Type': 'application/json' }
      });
      
      await fetchData();
      setSuccessMessage("Data added successfully!");
    } catch (error) {
      console.error('There was a problem adding data:', error);
      const errorMessage = JSON.parse(error.response.request.responseText).message;
      setFailMessage(`Failed to add data: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  } 
//#endregion




// #region Update function
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  
  const openUpdateForm = () => {
    setShowUpdateForm(true);
  }
  const closeUpdateForm = () => {
    setShowUpdateForm(false);
    resetVariables();
  }

  const fetchDataById = useCallback(async (_data) => {
    try {
      const response = await axios.get(`${apiUrl}/api/BankReconciliation/${_data?.id}` , {
        headers: { 'Content-Type': 'application/json'}
      })
      
      return response.data
    } catch (error) {
      console.error('Error fetching data:', error);
      return null
    }
  }, []);

  const editHandleClick = async (data) => {
    setLoading(true);

    const _data = await fetchDataById(data)
    setSelectedData(_data ?? null);
    setDate(_data?.date ?? null);
    setCheckNo(_data?.checkNo ?? "");
    setSequenceNo(_data?.sequenceNo ?? "");
    setReferenceNo(_data?.referenceNo ?? "");
    setBranch(_data?.branch ?? "");
    setTransactionCode(_data?.transactionCode ?? "");
    setTransactionDescription(_data?.transactionDescription ?? "");
    setWithdrawals(_data?.withdrawals ?? 0);
    setDeposits(_data?.deposits ?? 0);
    setRunningBalance(_data?.runningBalance ?? 0);

    openUpdateForm();
    setLoading(false);
  }

  const handleUpdateData = async () => {
    const data = {
      date: date,
      checkNo: checkNo,
      sequenceNo: sequenceNo,
      referenceNo: referenceNo,
      branch: branch,
      transactionCode: transactionCode,
      transactionDescription: transactionDescription,
      withdrawals: withdrawals,
      deposits: deposits,
      runningBalance: runningBalance,
    };

    try {
      setLoading(true);

      await axios.post(`${apiUrl}/api/BankReconciliation/update/${selectedData?.id}`, data, {
          headers: { 'Content-Type': 'application/json' }
      });
      
      await fetchData();
      setSuccessMessage("Data updated successfully!");
    } catch (error) {
      console.error('There was a problem adding data:', error);
      const errorMessage = JSON.parse(error.response.request.responseText).message;
      setFailMessage(`Failed to add data: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  } 
//#endregion




//#region Table data
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);

  const headers = [
    'ID', 
    'DATE', 
    'CHECK NO', 
    'SEQ NO', 
    'REF NO', 
    'BRANCH', 
    'TRANSACTION', 
    'WITHRAWALS', 
    'DEPOSITS', 
    'RUNNING BALANCE', 
    'ACTIONS',
  ]
  const accessors = [
    'id', 
    'date', 
    'checkNo', 
    'sequenceNo', 
    'referenceNo', 
    'branch', 
    'transaction', 
    'withdrawals', 
    'deposits', 
    'runningBalance', 
    'actions'
  ]

  const actions = [
    { 
      name: "Edit", 
      tooltip: "edit",
      icon: <EditIcon fontSize="small" />,
      className: "bg-green-500 hover:bg-green-600",
      function: editHandleClick,
      visible: true,
    },
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/BankReconciliation` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setData(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData])
//#endregion






//#region Import
  const [showImportForm, setShowImportForm] = useState(false)
  const [importData, setImportData] = useState([])

  const openImportForm = () => {
    setShowImportForm(true);
  }
  const closeImportForm = () => {
    setShowImportForm(false);
  }

  const handleProcessImport = async () => {
    if (importData.length <= 0) {
      setFailMessage("No data to import");
      return;
    }

    try {
      setLoading(true);

      const dataToImport = importData.map((item) => {
        let formattedDate = null;

        if (item.date) {
          const parts = String(item.date).split("/");

          if (parts.length === 3) {
            const month = Number(parts[0]);
            const day = Number(parts[1]);
            const year = Number(parts[2]);

            formattedDate = new Date(
              year,
              month - 1,
              day
            ).toISOString();
          }
        }

        return {
          ...item,
          date: formattedDate,
        };
      });

      await axios.post(
        `${apiUrl}/api/BankReconciliation/bulk`,
        dataToImport,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      await fetchData();

      setSuccessMessage("Data imported successfully!");
    } catch (error) {
      console.error(
        "There was a problem importing data:",
        error
      );

      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.errors ||
        error?.message ||
        "An unknown error occurred.";

      setFailMessage(
        `Failed to import data: ${
          typeof errorMessage === "string"
            ? errorMessage
            : JSON.stringify(errorMessage)
        }`
      );
    } finally {
      setLoading(false);
      setImportData([]);
    }
  };
//#endregion








//#region Access Template
  const accountData = JSON.parse(localStorage.getItem("accountData"));
  
  if (!accountData?.accessTemplate?.hasBankReconciliation) {
    return <NoAccess />;
  }
//#endregion





//#region UI
  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <AssuredWorkloadIcon fontSize="large" className="text-theme-color" />
              {`Bank Reconciliation(${data?.length ?? 0})`}
            </h2>
            <p className="text-gray-500 mt-1">Matching your accounting transactions with the bank statement.</p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={openImportForm}
              className="flex items-center gap-1.5 px-4 py-2 text-gray-500 hover:text-theme-color hover:bg-gray-100 rounded-md text-sm font-medium transition-all border"
            >
              <PublishIcon sx={{ fontSize: 16 }} />
              Import Bank Statement
            </button>

            <button onClick={openAddForm} className="flex items-center gap-2 bg-theme-color text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-theme-color-h transition shadow-md">
              <AddCircleOutlineIcon fontSize="small" /> New Transaction
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex justify-between items-center gap-2">
          <div className="mb-4 w-full">
            <input
              type="text"
              placeholder="Search by name or ID..."
              className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
            />
            {/* Add filter dropdowns here if needed */}
          </div>
        </div>

        <CreateTable
          tableName={"Bank Reconciliation"}
          headers={headers}
          accessors={accessors}
          data={data}
          actions={actions}

          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          itemsPerPage={10}
          totalItems={data?.length}
        />
      </div>

      <AddUpdateModal 
        visible={showAddForm}
        onClose={closeAddForm}
        onSave={handleAddData}
        modalName={"Add Data"}

        date={date} setDate={setDate}
        checkNo={checkNo} setCheckNo={setCheckNo}
        sequenceNo={sequenceNo} setSequenceNo={setSequenceNo}
        referenceNo={referenceNo} setReferenceNo={setReferenceNo}
        branch={branch} setBranch={setBranch}
        transactionCode={transactionCode} setTransactionCode={setTransactionCode}
        transactionDescription={transactionDescription} setTransactionDescription={setTransactionDescription}
        withdrawals={withdrawals} setWithdrawals={setWithdrawals}
        deposits={deposits} setDeposits={setDeposits}
        runningBalance={runningBalance} setRunningBalance={setRunningBalance}
      />

      <AddUpdateModal 
        visible={showUpdateForm}
        onClose={closeUpdateForm}
        onSave={handleUpdateData}
        modalName={"Update Data"}

        data={selectedData}
        date={date} setDate={setDate}
        checkNo={checkNo} setCheckNo={setCheckNo}
        sequenceNo={sequenceNo} setSequenceNo={setSequenceNo}
        referenceNo={referenceNo} setReferenceNo={setReferenceNo}
        branch={branch} setBranch={setBranch}
        transactionCode={transactionCode} setTransactionCode={setTransactionCode}
        transactionDescription={transactionDescription} setTransactionDescription={setTransactionDescription}
        withdrawals={withdrawals} setWithdrawals={setWithdrawals}
        deposits={deposits} setDeposits={setDeposits}
        runningBalance={runningBalance} setRunningBalance={setRunningBalance}

      />

      <ImportModal 
        isOpen={showImportForm} 
        onClose={closeImportForm} 
        onImport={handleProcessImport} 
        importData={importData} setImportData={setImportData}
      />

      <div className="fixed top-5 right-5 z-50 flex flex-col space-y-2">
        <SuccessMessage message={successMessage} onClose={() => setSuccessMessage("")} />
        <FailMessage message={failMessage} onClose={() => setFailMessage("")} />
      </div>

      { loading && <Loading />}
    </>
  );
//#endregion
}







//#region AddUpdate Modal
const AddUpdateModal = ({
  visible, onClose, onSave, modalName,
  data,
  date, setDate,
  checkNo, setCheckNo,
  sequenceNo, setSequenceNo,
  referenceNo, setReferenceNo,
  branch, setBranch,
  transactionCode, setTransactionCode,
  transactionDescription, setTransactionDescription,
  withdrawals, setWithdrawals,
  deposits, setDeposits,
  runningBalance, setRunningBalance,
}) => {
  const [loading, setLoading] = useState(false);
  const getCurrency = JSON.parse(localStorage.getItem("currency") || "{}");
  const [failMessage, setFailMessage] = useState("");
  const [bank, setBank] = useState(null);

  // If in Update mode with existing bank/data, or bank has been successfully generated
  const isGenerated = Boolean(bank || (data && modalName?.toLowerCase().includes("update")));

  const fetchBankByCheckNo = useCallback(async () => {
    if (!checkNo) {
      setFailMessage("Please enter a Check No. first.");
      return null;
    }

    try {
      const response = await axios.get(
        `${apiUrl}/api/BankReconciliation/get-transaction/check-no/${checkNo}`,
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (!response.data) {
        setFailMessage("No bank found for this Check No.");
        return null;
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching bank data:', error);
      setFailMessage("Failed to fetch bank by Check No. Please verify and try again.");
      return null;
    }
  }, [checkNo]);

  const generateHandleClick = async () => {
    if (!checkNo) {
      setFailMessage("Please enter Check No. first.");
      return;
    }
    setLoading(true);
    setFailMessage("");
    const transaction = await fetchBankByCheckNo();
    setBank(transaction?.bank);

    if(transaction?.type === "Payable"){
      setWithdrawals(transaction?.amount ?? 0)
    }

    if(transaction?.type === "Receivable"){
      setDeposits(transaction?.amount ?? 0)
    }

    // Optional: Auto-fill branch if provided by the bank endpoint
    if (transaction?.bank?.branch && !branch) {
      setBranch(transaction?.bank.branch);
    }

    setLoading(false);
  };

  const handleSubmit = async () => {
    // Validation check
    if (!date || !checkNo) {
      setFailMessage("Please fill up required field/s.");
      return;
    }
    if (!isGenerated) {
      setFailMessage("Please generate bank information from Check No. before saving.");
      return;
    }
    await onSave();
    closeModal();
  };

  const resetLocalVariables = () => {
    setLoading(false);
    setFailMessage("");
    setBank(null);
  };

  const closeModal = () => {
    resetLocalVariables();
    onClose();
  };

  useEffect(() => {
    if (visible && bank) {
      let _runningBalance;

      if (!data) {
        // New reconciliation
        _runningBalance =
          Number(bank?.balance ?? 0) -
          Number(withdrawals ?? 0) +
          Number(deposits ?? 0);
      } else {
        // Existing reconciliation
        _runningBalance =
          Number(bank?.balance ?? 0) +
          Number(data?.withdrawals ?? 0) -
          Number(data?.deposits ?? 0) -
          Number(withdrawals ?? 0) +
          Number(deposits ?? 0);
      }

      setRunningBalance(_runningBalance);
    }
  }, [
    visible,
    bank,
    withdrawals,
    deposits,
    data,
    setRunningBalance
  ]);

  useEffect(() => {
    if(visible && data){
      setBank(data?.bank)
    }
  }, [visible, data])

  const oldBankBalance = data
    ? Number(bank?.balance ?? 0) +
      Number(data?.withdrawals ?? 0) -
      Number(data?.deposits ?? 0)
    : Number(bank?.balance ?? 0);

  if (!visible) return null;

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            className="fixed inset-0 bg-gray-900 bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gray-100 rounded-2xl shadow-2xl w-full max-w-7xl overflow-hidden flex flex-col max-h-[90vh]"
            >
              {/* Header */}
              <div className="px-8 py-5 border-b border-gray-300">
                <h2 className="text-2xl font-semibold text-gray-800">
                  {modalName}
                </h2>
              </div>

              <div className="p-8 overflow-y-auto custom-scrollbar">
                {/* --- FIRST PART: Check No. & Date --- */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6 border-b border-gray-200">
                  {/* Check No with Generate Button */}
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">
                      Check No. <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="e.g. 00000000"
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-theme-color transition"
                        value={checkNo}
                        onChange={(e) => {
                          setCheckNo(e.target.value);
                          if (bank) setBank(null); // Reset verification if check number changes
                        }}
                      />
                      <button
                        type="button"
                        onClick={generateHandleClick}
                        disabled={!checkNo || loading}
                        className="px-5 py-2 bg-theme-color text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-sm whitespace-nowrap"
                      >
                        Generate
                      </button>
                    </div>

                    {/* Bank Info Badge once generated */}
                    {bank && (
                      <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-emerald-900">
                        {/* Bank Info */}
                        <div className="flex items-center gap-3">
                          <div className="p-2.5 bg-emerald-100 text-emerald-700 rounded-xl flex items-center justify-center flex-shrink-0">
                            {/* Bank / Card Icon */}
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                            </svg>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-base text-emerald-950">
                                {bank.bankName || bank.name || bank.accountName || "Bank Found"}
                              </span>
                              <span className="text-xs px-2 py-0.5 bg-emerald-200 text-emerald-800 rounded-full font-semibold">
                                ✓ Verified
                              </span>
                            </div>
                            {bank.accountNo && (
                              <p className="text-xs text-emerald-700 font-mono mt-0.5">
                                Account No: <span className="font-semibold">{bank.accountNo}</span>
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Bank Balance Section */}
                        <div className="bg-white/90 border border-emerald-200 rounded-lg px-4 py-2 flex sm:flex-col justify-between items-center sm:items-end shadow-xs min-w-[160px]">
                          <span className="text-xs font-medium text-emerald-700 uppercase tracking-wider">
                            {`${data ? "Old" : ""} Bank Balance`}
                          </span>

                          <span className="text-lg font-bold text-emerald-900 font-mono">
                            {getCurrency?.symbol ?? ""}
                            {typeof FormatValueToDecimal === "function"
                              ? FormatValueToDecimal(oldBankBalance)
                              : oldBankBalance.toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Date */}
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">
                      Date <span className="text-red-500">*</span>
                    </label>
                    <DateSelect data={date} setData={setDate} isEditable={true} />
                  </div>
                </div>

                {/* Notice banner when not yet generated */}
                {!isGenerated && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm flex items-center gap-2">
                    <svg className="w-5 h-5 flex-shrink-0 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <span>
                      Please enter a <strong>Check No.</strong> and click <strong>Generate</strong> to identify the bank and unlock the remaining fields.
                    </span>
                  </div>
                )}

                {/* --- SECOND PART: Remaining Fields (Disabled until generated) --- */}
                <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 transition-opacity duration-200 ${!isGenerated ? "opacity-50 pointer-events-none select-none" : ""}`}>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">
                      Sequence No.
                    </label>
                    <input
                      type="text"
                      disabled={!isGenerated}
                      placeholder="e.g. 00000000"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-theme-color transition disabled:bg-gray-100"
                      value={sequenceNo}
                      onChange={(e) => setSequenceNo(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">
                      Reference No.
                    </label>
                    <input
                      type="text"
                      disabled={!isGenerated}
                      placeholder="e.g. 00000000"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-theme-color transition disabled:bg-gray-100"
                      value={referenceNo}
                      onChange={(e) => setReferenceNo(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">
                      Branch
                    </label>
                    <input
                      type="text"
                      disabled={!isGenerated}
                      placeholder="e.g. PNB Manila"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-theme-color transition disabled:bg-gray-100"
                      value={branch}
                      onChange={(e) => setBranch(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">
                      Transaction Code
                    </label>
                    <input
                      type="text"
                      disabled={!isGenerated}
                      placeholder="e.g. BATCH0000"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-theme-color transition disabled:bg-gray-100"
                      value={transactionCode}
                      onChange={(e) => setTransactionCode(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">
                      Transaction Description
                    </label>
                    <input
                      type="text"
                      disabled={!isGenerated}
                      placeholder="e.g. LOCAL"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-theme-color transition disabled:bg-gray-100"
                      value={transactionDescription}
                      onChange={(e) => setTransactionDescription(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-1 text-gray-700">Withdrawals</label>
                    <input
                      type="number"
                      disabled={!isGenerated}
                      placeholder="0.00"
                      className="text-sm w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-theme-color transition disabled:bg-gray-100"
                      value={withdrawals}
                      onChange={(e) => setWithdrawals(e.target.value)}
                    />
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <span className="font-semibold text-gray-400 italic">
                        {getCurrency?.symbol ?? ""}
                        {FormatValueToDecimal(withdrawals || 0)}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-1 text-gray-700">Deposits</label>
                    <input
                      type="number"
                      disabled={!isGenerated}
                      placeholder="0.00"
                      className="text-sm w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-theme-color transition disabled:bg-gray-100"
                      value={deposits}
                      onChange={(e) => setDeposits(e.target.value)}
                    />
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <span className="font-semibold text-gray-400 italic">
                        {getCurrency?.symbol ?? ""}
                        {FormatValueToDecimal(deposits || 0)}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-1 text-gray-700">Running Balance</label>
                    <input
                      type="number"
                      placeholder="0.00"
                      className="text-sm w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-theme-color transition"
                      value={runningBalance}
                      onChange={(e) => setRunningBalance(e.target.value)}
                      disabled
                    />
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <span className="font-semibold text-gray-400 italic">
                        {getCurrency?.symbol ?? ""}
                        {FormatValueToDecimal(runningBalance || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 p-6 border-t border-gray-300 bg-gray-50">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-200 transition font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!isGenerated || loading}
                  className="px-8 py-2 rounded-lg bg-theme-color text-white font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-md"
                >
                  Save
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="fixed top-5 right-5 z-[9999]">
        <FailMessage message={failMessage} onClose={() => setFailMessage("")} />
      </div>

      {loading && <Loading />}
    </>
  );
};
//#endregion












//#region Import Modal
const ImportModal = ({ isOpen, onClose, onImport, importData, setImportData }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState([]); // Store rows for preview
  const [isDragging, setIsDragging] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  // Function to read the Excel file and generate a preview
  const generatePreview = async (selectedFile) => {
    setLoadingPreview(true);

    try {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const buffer = e.target.result;

          // Supports both .xls and .xlsx
          const workbook = XLSX.read(buffer, {
            type: "array",
            cellDates: true,
            raw: true,
          });

          if (!workbook.SheetNames?.length) {
            throw new Error("No worksheet found in the selected file.");
          }

          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          if (!worksheet) {
            throw new Error("No worksheet found in the selected file.");
          }

          // Convert worksheet to array of rows
          const excelRows = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: "",
            raw: true,
          });

          // Find the actual transaction header row
          const headerIndex = excelRows.findIndex((row) => {
            return (
              String(row[1] ?? "").trim().toLowerCase() === "date" &&
              String(row[2] ?? "").trim().toLowerCase().includes("check/seq") &&
              String(row[4] ?? "").trim().toLowerCase() ===
                "sba reference no." &&
              String(row[5] ?? "").trim().toLowerCase() ===
                "negotiating branch" &&
              String(row[6] ?? "").trim().toLowerCase() ===
                "transaction code" &&
              String(row[7] ?? "").trim().toLowerCase() ===
                "transaction description" &&
              String(row[8] ?? "").trim().toLowerCase() === "withdrawals" &&
              String(row[9] ?? "").trim().toLowerCase() === "deposits" &&
              String(row[10] ?? "").trim().toLowerCase() ===
                "running balance"
            );
          });

          if (headerIndex === -1) {
            throw new Error(
              "Transaction header row was not found in the selected file."
            );
          }

          // Convert amount values to numbers
          const toNumber = (value) => {
            if (value === null || value === undefined || value === "") {
              return 0;
            }

            return Number(String(value).replace(/,/g, "").trim()) || 0;
          };

          const rows = [];

          // Read rows after the transaction header
          for (let i = headerIndex + 1; i < excelRows.length; i++) {
            const row = excelRows[i];

            if (!row) {
              continue;
            }

            // Skip completely empty rows
            const isEmpty = row.every(
              (cell) => String(cell ?? "").trim() === ""
            );

            if (isEmpty) {
              continue;
            }

            // Actual column mapping
            const date = row[1];
            const checkSeqNo = String(row[2] ?? "").trim();
            const referenceNo = row[4];
            const branch = row[5];
            const transactionCode = row[6];
            const transactionDescription = row[7];
            const withdrawals = row[8];
            const deposits = row[9];
            const runningBalance = row[10];

            // Skip rows without a date
            if (!date) {
              continue;
            }

            // Split Check/Seq. No.
            // Example: 2000013434/73736
            //          checkNo / sequenceNo
            const [checkNo = "", sequenceNo = ""] =
              checkSeqNo.split("/");

            rows.push({
              date: date,
              checkNo: checkNo.trim(),
              sequenceNo: sequenceNo.trim(),
              referenceNo: String(referenceNo ?? "").trim(),
              branch: String(branch ?? "").trim(),
              transactionCode: String(transactionCode ?? "").trim(),
              transactionDescription: String(
                transactionDescription ?? ""
              ).trim(),
              withdrawals: toNumber(withdrawals),
              deposits: toNumber(deposits),
              runningBalance: toNumber(runningBalance),
            });
          }

          setImportData(rows)
          setPreviewData(rows);
          setLoadingPreview(false);
        } catch (error) {
          console.error("Preview failed:", error);

          setPreviewData([]);
          setLoadingPreview(false);
        }
      };

      reader.onerror = () => {
        console.error("Failed to read file.");

        setPreviewData([]);
        setLoadingPreview(false);
      };

      reader.readAsArrayBuffer(selectedFile);
    } catch (error) {
      console.error("Preview failed:", error);

      setPreviewData([]);
      setLoadingPreview(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      generatePreview(selectedFile);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      generatePreview(droppedFile);
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    setFile(null);
    setPreviewData([]);
  };

  const handleUpload = () => {
    if (file) {
      onImport(file);
      setFile(null);
      setPreviewData([]);
      onClose();
    }
  };

  const handleClose = (e) => {
    onClose()
    handleRemove(e)
    setCurrentPage(1)
  }

  const headers = [
    'Date', 
    'Check No', 
    'Sequence No', 
    'Reference No', 
    'Branch', 
    'Transaction Code', 
    'Transaction Description', 
    'Withdrawals', 
    'Deposits', 
    'Running Balance', 
  ]
  const accessors = [
    'date', 
    'checkNo', 
    'sequenceNo', 
    'referenceNo', 
    'branch', 
    'transactionCode', 
    'transactionDescription', 
    'withdrawals', 
    'deposits', 
    'runningBalance', 
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-white">
          <h2 className="text-lg font-semibold text-gray-800">Import Bank Statement</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <CloseIcon size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto">
          {!file ? (
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current.click()}
              className={`border-2 border-dashed rounded-lg p-12 flex flex-col items-center justify-center cursor-pointer transition-all ${
                isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-blue-400"
              }`}
            >
              <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".xlsx, .xls" className="hidden" />
              <div className="bg-blue-100 p-3 rounded-full text-blue-600 mb-4">
                <FileUploadIcon size={28} />
              </div>
              <p className="text-gray-700 font-medium">Please select file or drop down file</p>
              <p className="text-gray-400 text-sm mt-1">Excel templates only (.xlsx)</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* File Info Card */}
              <div className="flex items-center justify-between bg-blue-50 border border-blue-100 p-4 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileCopyIcon className="text-blue-600" size={24} />
                  <div>
                    <p className="text-blue-900 font-semibold text-sm">{file.name}</p>
                    <p className="text-blue-700 text-xs">{(file.size / 1024).toFixed(2)} KB</p>
                  </div>
                </div>
                <button onClick={handleRemove} className="text-red-500 text-sm font-medium hover:underline">Change File</button>
              </div>

              {/* Preview Table */}
              <div>
                <div className="flex items-center gap-2 mb-2 text-gray-600">
                  <PreviewIcon size={16} />
                  <span className="text-xs font-bold uppercase tracking-wider">Data Preview</span>
                </div>
                
                {loadingPreview ? (
                  <div className="h-32 flex items-center justify-center text-gray-400">Loading preview...</div>
                ) : (
                  <div className="border rounded-lg overflow-x-auto bg-gray-50">
                    <table className="min-w-full divide-y divide-gray-200 text-[11px] hidden">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-3 py-2 text-left font-bold text-gray-600">Date</th>
                          <th className="px-3 py-2 text-left font-bold text-gray-600">Check No</th>
                          <th className="px-3 py-2 text-left font-bold text-gray-600">Sequence No</th>
                          <th className="px-3 py-2 text-left font-bold text-gray-600">Reference No</th>
                          <th className="px-3 py-2 text-left font-bold text-gray-600">Branch</th>
                          <th className="px-3 py-2 text-left font-bold text-gray-600">Transaction Code</th>
                          <th className="px-3 py-2 text-left font-bold text-gray-600">Transaction Description</th>
                          <th className="px-3 py-2 text-left font-bold text-gray-600">Withdrawals</th>
                          <th className="px-3 py-2 text-left font-bold text-gray-600">Deposits</th>
                          <th className="px-3 py-2 text-left font-bold text-gray-600">Running Balance</th>
                          {/* <th className="px-3 py-2 text-left font-bold text-gray-600">Average Cost</th> */}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {previewData.slice(1).map((row, i) => {
                          return (
                            <tr key={i}>
                              <td className="px-3 py-2 whitespace-nowrap text-gray-700">{row.date}</td>
                              <td className="px-3 py-2 text-gray-500 truncate max-w-[150px]">{row.checkNo}</td>
                              <td className="px-3 py-2 text-gray-500">{row.sequenceNo}</td>
                              <td className="px-3 py-2 text-gray-500">{row.referenceNo}</td>
                              <td className="px-3 py-2 text-gray-500">{row.branch}</td>
                              <td className="px-3 py-2 text-gray-500">{row.transactionCode}</td>
                              <td className="px-3 py-2 text-gray-500">{row.transactionDescription}</td>
                              <td className="px-3 py-2 text-blue-600 font-medium">{row.withdrawals}</td>
                              <td className="px-3 py-2 text-blue-600 font-medium">{row.deposits}</td>
                              <td className="px-3 py-2 text-blue-600 font-medium">{row.runningBalance}</td>
                            </tr>
                        )})}
                      </tbody>
                    </table>
                    <CreateTable
                      tableName={"Bank Reconciliation"}
                      headers={headers}
                      accessors={accessors}
                      data={previewData}
                      actions={[]}

                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      itemsPerPage={10}
                      totalItems={previewData?.length}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
          <button onClick={handleClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800">Cancel</button>
          <button
            onClick={handleUpload}
            disabled={!file || loadingPreview}
            className={`px-6 py-2 rounded-lg text-sm font-bold text-white transition-all ${
              file ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-300 cursor-not-allowed"
            }`}
          >
            Import Now
          </button>
        </div>
      </div>
    </div>
  );
};
//#endregion